/**
 * useSoundSettings — Zustand store for sound volume, mute, and quiet hours
 * Persisted to localStorage under 'mindora-sound-settings'
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface QuietHours {
  enabled: boolean;
  start: string; // "HH:MM" 24h format
  end: string;
}

interface SoundSettingsState {
  masterVolume: number;
  sfxEnabled: boolean;
  sfxVolume: number;
  ambientEnabled: boolean;
  ambientVolume: number;
  companionEnabled: boolean;
  companionVolume: number;
  quietHours: QuietHours;
  // Computed
  isQuietTime: () => boolean;
  effectivelyMuted: () => boolean;
  // Actions
  setMasterVolume: (v: number) => void;
  toggleSfx: () => void;
  setSfxVolume: (v: number) => void;
  toggleAmbient: () => void;
  setAmbientVolume: (v: number) => void;
  toggleCompanion: () => void;
  setCompanionVolume: (v: number) => void;
  setQuietHours: (qh: Partial<QuietHours>) => void;
}

function isInQuietHours(qh: QuietHours): boolean {
  if (!qh.enabled) return false;
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const [startH, startM] = qh.start.split(':').map(Number);
  const [endH, endM] = qh.end.split(':').map(Number);
  const startMin = startH * 60 + startM;
  const endMin = endH * 60 + endM;

  if (startMin <= endMin) {
    // Same-day range (e.g., 08:00 - 20:00)
    return currentMinutes >= startMin && currentMinutes < endMin;
  }
  // Overnight range (e.g., 20:00 - 07:00)
  return currentMinutes >= startMin || currentMinutes < endMin;
}

export const useSoundSettings = create<SoundSettingsState>()(
  persist(
    (set, get) => ({
      masterVolume: 0.7,
      sfxEnabled: true,
      sfxVolume: 0.8,
      ambientEnabled: false,
      ambientVolume: 0.3,
      companionEnabled: true,
      companionVolume: 0.6,
      quietHours: { enabled: false, start: '20:00', end: '07:00' },

      isQuietTime: () => isInQuietHours(get().quietHours),
      effectivelyMuted: () => get().masterVolume === 0 || isInQuietHours(get().quietHours),

      setMasterVolume: (v) => set({ masterVolume: Math.max(0, Math.min(1, v)) }),
      toggleSfx: () => set((s) => ({ sfxEnabled: !s.sfxEnabled })),
      setSfxVolume: (v) => set({ sfxVolume: Math.max(0, Math.min(1, v)) }),
      toggleAmbient: () => set((s) => ({ ambientEnabled: !s.ambientEnabled })),
      setAmbientVolume: (v) => set({ ambientVolume: Math.max(0, Math.min(1, v)) }),
      toggleCompanion: () => set((s) => ({ companionEnabled: !s.companionEnabled })),
      setCompanionVolume: (v) => set({ companionVolume: Math.max(0, Math.min(1, v)) }),
      setQuietHours: (qh) => set((s) => ({ quietHours: { ...s.quietHours, ...qh } })),
    }),
    {
      name: 'mindora-sound-settings',
      partialize: (state) => ({
        masterVolume: state.masterVolume,
        sfxEnabled: state.sfxEnabled,
        sfxVolume: state.sfxVolume,
        ambientEnabled: state.ambientEnabled,
        ambientVolume: state.ambientVolume,
        companionEnabled: state.companionEnabled,
        companionVolume: state.companionVolume,
        quietHours: state.quietHours,
      }),
    },
  ),
);
